/**
 * Theme Toggle Component
 *
 * A button component that allows users to switch between light and dark
 * color modes. Uses Chakra UI's useColorMode hook for state management
 * and localStorage persistence.
 *
 * @fileoverview Dark/Light mode toggle button component
 *
 * @example
 * // Basic usage in a header or navigation
 * import { ThemeToggle } from './components/ThemeToggle';
 *
 * function Header() {
 *   return (
 *     <nav>
 *       <ThemeToggle />
 *     </nav>
 *   );
 * }
 */

import React from 'react';
import {
  IconButton,
  useColorMode,
  useColorModeValue,
  Tooltip,
  type IconButtonProps,
} from '@chakra-ui/react';
import { SunIcon, MoonIcon } from '@chakra-ui/icons';

/**
 * Props interface for ThemeToggle component
 *
 * Extends IconButtonProps to allow passing additional button props
 * like size, variant, or aria-label overrides.
 */
interface ThemeToggleProps extends Omit<IconButtonProps, 'aria-label' | 'icon' | 'onClick'> {
  /**
   * Show tooltip on hover
   * @default true
   */
  showTooltip?: boolean;

  /**
   * Custom label for light mode
   * @default 'Switch to light mode'
   */
  lightModeLabel?: string;

  /**
   * Custom label for dark mode
   * @default 'Switch to dark mode'
   */
  darkModeLabel?: string;
}

/**
 * ThemeToggle Component
 *
 * A reusable button that toggles between light and dark color modes.
 * The button displays:
 * - SunIcon when in dark mode (click to switch to light)
 * - MoonIcon when in light mode (click to switch to dark)
 *
 * ## Features
 *
 * 1. **Automatic Icon Switching**: Icon changes based on current mode
 * 2. **Tooltip Support**: Shows helpful text on hover
 * 3. **Accessibility**: Includes proper aria-label for screen readers
 * 4. **Persistence**: Color mode is saved to localStorage automatically
 * 5. **Customizable**: Accepts all IconButton props for styling
 *
 * ## How It Works
 *
 * - Uses Chakra's `useColorMode` hook to get/set the color mode
 * - The mode is automatically persisted to localStorage
 * - ColorModeScript in the provider ensures correct mode on page load
 *
 * ## Accessibility
 *
 * The component includes:
 * - Dynamic aria-label describing the action
 * - Tooltip for visual hint
 * - Focus ring for keyboard navigation
 *
 * @param props - Component props
 * @param props.showTooltip - Whether to show tooltip (default: true)
 * @param props.lightModeLabel - Label for light mode switch
 * @param props.darkModeLabel - Label for dark mode switch
 *
 * @returns JSX.Element - The theme toggle button
 *
 * @example
 * // Default usage
 * <ThemeToggle />
 *
 * @example
 * // With custom size and variant
 * <ThemeToggle size="lg" variant="ghost" />
 *
 * @example
 * // Without tooltip
 * <ThemeToggle showTooltip={false} />
 *
 * @example
 * // With custom labels
 * <ThemeToggle
 *   lightModeLabel="Day mode"
 *   darkModeLabel="Night mode"
 * />
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  showTooltip = true,
  lightModeLabel = 'Switch to light mode',
  darkModeLabel = 'Switch to dark mode',
  ...iconButtonProps
}) => {
  /**
   * useColorMode Hook
   *
   * Returns the current color mode and a function to toggle it.
   * - colorMode: 'light' | 'dark'
   * - toggleColorMode: () => void - Toggles between modes
   *
   * The mode is automatically persisted to localStorage under
   * the key 'chakra-ui-color-mode'.
   */
  const { colorMode, toggleColorMode } = useColorMode();

  /**
   * Determine if currently in dark mode
   */
  const isDarkMode = colorMode === 'dark';

  /**
   * useColorModeValue Hook
   *
   * Returns different values based on the current color mode.
   * First argument is the light mode value, second is dark mode.
   *
   * Used here for:
   * - Icon selection (MoonIcon for light, SunIcon for dark)
   * - Aria-label text (contextual to current mode)
   */
  const icon = useColorModeValue(
    <MoonIcon />,   // Shown in light mode (click to go dark)
    <SunIcon />     // Shown in dark mode (click to go light)
  );

  /**
   * Dynamic aria-label for accessibility
   * Describes what clicking the button will do
   */
  const ariaLabel = useColorModeValue(
    darkModeLabel,  // In light mode, clicking switches to dark
    lightModeLabel  // In dark mode, clicking switches to light
  );

  /**
   * Tooltip label (same as aria-label for consistency)
   */
  const tooltipLabel = useColorModeValue(
    darkModeLabel,
    lightModeLabel
  );

  /**
   * Button background colors for each mode
   * Creates subtle visual distinction
   */
  const buttonBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const buttonHoverBg = useColorModeValue('gray.200', 'whiteAlpha.300');

  /**
   * The toggle button element
   *
   * Uses IconButton for icon-only button with proper accessibility
   */
  const toggleButton = (
    <IconButton
      aria-label={ariaLabel}
      icon={icon}
      onClick={toggleColorMode}
      variant="ghost"
      size="md"
      borderRadius="md"
      bg={buttonBg}
      _hover={{
        bg: buttonHoverBg,
      }}
      _focus={{
        boxShadow: 'outline',
      }}
      // Pass through any additional props
      {...iconButtonProps}
    />
  );

  /**
   * Wrap in Tooltip if showTooltip is true
   *
   * The tooltip provides a visual hint about what clicking
   * the button will do.
   */
  if (showTooltip) {
    return (
      <Tooltip
        label={tooltipLabel}
        placement="bottom"
        hasArrow
        openDelay={300}
      >
        {toggleButton}
      </Tooltip>
    );
  }

  return toggleButton;
};

/**
 * useThemeMode Custom Hook
 *
 * A convenience hook that wraps useColorMode with additional utilities.
 * Useful when you need more control over theme state.
 *
 * @returns Object with colorMode, toggleColorMode, and helper booleans
 *
 * @example
 * const { isDark, isLight, toggle } = useThemeMode();
 *
 * return (
 *   <Box bg={isDark ? 'gray.800' : 'white'}>
 *     <Button onClick={toggle}>Toggle Theme</Button>
 *   </Box>
 * );
 */
export const useThemeMode = () => {
  const { colorMode, toggleColorMode, setColorMode } = useColorMode();

  return {
    /** Current color mode value */
    colorMode,

    /** Whether currently in dark mode */
    isDark: colorMode === 'dark',

    /** Whether currently in light mode */
    isLight: colorMode === 'light',

    /** Toggle between light and dark modes */
    toggle: toggleColorMode,

    /** Set a specific color mode */
    setMode: setColorMode,

    /** Set to dark mode */
    setDark: () => setColorMode('dark'),

    /** Set to light mode */
    setLight: () => setColorMode('light'),
  };
};

/**
 * Default export for simpler imports
 */
export default ThemeToggle;
